import React, { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  InputLabel,
  MenuItem,
  Select,
  Slider,
  TextField,
  Typography,
} from "@mui/material";
import { Controller, useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useNavigate } from "react-router-dom";
import { FormLabel } from "../../components/form-label/FormLabel";
import { FlowSelect } from "../../components/flow-select/FlowSelect";
import { provisioningService } from "../../services/provisioningService";
import { tenantService } from "../../services/tenantService";
import { flowService } from "../../services/flowService";
import { Chatbot, Flow, Tenant } from "../../types";
import { required_error } from "../../services/shared";

const schema = z.object({
  tenant_id: z.string({ required_error }).min(1, "Seleziona un tenant"),
  system_prompt: z.string({ required_error }).min(10, "Minimo 10 caratteri"),
  model: z.enum(["gpt-4o", "gpt-4o-mini", "gpt-4-turbo", "gpt-terra"]),
  temperature: z.number().min(0).max(2),
  max_tokens: z
    .number({ required_error, invalid_type_error: "Numero richiesto" })
    .int()
    .min(1)
    .max(32000),
  max_history: z
    .number({ required_error, invalid_type_error: "Numero richiesto" })
    .int()
    .min(1)
    .max(50),
  flow_id: z.string().nullable(),
});

type FormData = z.infer<typeof schema>;

export default function ChatbotCreate() {
  const navigate = useNavigate();
  const [tenants, setTenants] = useState<Tenant[]>([]);
  const [flows, setFlows] = useState<Flow[]>([]);
  const [error, setError] = useState<string | null>(null);

  const {
    control,
    handleSubmit,
    watch,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<FormData>({
    resolver: zodResolver(schema),
    defaultValues: {
      tenant_id: "",
      system_prompt: "",
      model: "gpt-4o",
      temperature: 0.7,
      max_tokens: 1000,
      max_history: 10,
      flow_id: null,
    },
  });

  const selectedTenantId = watch("tenant_id");

  useEffect(() => {
    tenantService
      .list()
      .then((res) => setTenants(res.data.data.tenants.filter((t) => t.active)))
      .catch((e) => {
        console.error(e);
        setError("Errore nel caricamento dei tenant");
      });
  }, []);

  // The flow list depends on the selected tenant: reload on change and reset the
  // selection so a stale flow_id from a previous tenant can't be submitted.
  useEffect(() => {
    setValue("flow_id", null);
    if (!selectedTenantId) {
      setFlows([]);
      return;
    }
    flowService
      .list(selectedTenantId)
      .then((res) => setFlows(res.data.data.flows))
      .catch((e) => {
        console.error(e);
        setError("Errore nel caricamento dei flow");
      });
  }, [selectedTenantId, setValue]);

  const onSubmit = handleSubmit(async (data) => {
    setError(null);
    try {
      await provisioningService.createChatbot({
        ...data,
        model: data.model as Chatbot["model"],
      });
      navigate("/chatbot/chatbots");
    } catch (e) {
      console.error(e);
      setError("Errore durante la creazione del chatbot");
    }
  });

  return (
    <div className="container">
      <FormLabel label="Nuovo Chatbot" />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Box
        sx={{
          background: "white",
          border: "1px solid #d1d1d1",
          borderRadius: ".5em",
          p: 3,
        }}
        className="flex-col gap-2"
      >
        <form onSubmit={onSubmit} className="flex-col gap-2">
          {/* Tenant */}
          <Controller
            name="tenant_id"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.tenant_id}>
                <InputLabel id="tenant-label">Tenant</InputLabel>
                <Select
                  {...field}
                  labelId="tenant-label"
                  label="Tenant"
                  value={field.value ?? ""}
                >
                  <MenuItem value="" disabled>
                    Seleziona un tenant
                  </MenuItem>
                  {tenants.map((t) => (
                    <MenuItem key={t.id} value={t.id}>
                      {t.name}
                    </MenuItem>
                  ))}
                </Select>
                {errors.tenant_id && (
                  <FormHelperText>{errors.tenant_id.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          {/* System prompt */}
          <Controller
            name="system_prompt"
            control={control}
            render={({ field }) => (
              <TextField
                {...field}
                fullWidth
                multiline
                rows={5}
                size="small"
                label="System Prompt"
                placeholder="Sei un assistente virtuale..."
                error={!!errors.system_prompt}
                helperText={errors.system_prompt?.message}
              />
            )}
          />

          {/* Model */}
          <Controller
            name="model"
            control={control}
            render={({ field }) => (
              <FormControl fullWidth size="small" error={!!errors.model}>
                <InputLabel id="model-label">Modello</InputLabel>
                <Select {...field} labelId="model-label" label="Modello">
                  <MenuItem value="gpt-4o">gpt-4o</MenuItem>
                  <MenuItem value="gpt-4o-mini">gpt-4o-mini</MenuItem>
                  <MenuItem value="gpt-4-turbo">gpt-4-turbo</MenuItem>
                  <MenuItem value="gpt-terra">gpt-terra</MenuItem>
                </Select>
                {errors.model && (
                  <FormHelperText>{errors.model.message}</FormHelperText>
                )}
              </FormControl>
            )}
          />

          {/* Temperature */}
          <Controller
            name="temperature"
            control={control}
            render={({ field }) => (
              <Box>
                <Typography variant="body2" gutterBottom>
                  Temperature: {field.value}
                </Typography>
                <Slider
                  value={field.value}
                  onChange={(_, v) => field.onChange(v)}
                  min={0}
                  max={2}
                  step={0.1}
                  marks={[
                    { value: 0, label: "0" },
                    { value: 1, label: "1" },
                    { value: 2, label: "2" },
                  ]}
                  valueLabelDisplay="auto"
                />
              </Box>
            )}
          />

          <div className="flex-row gap-2">
            {/* Max tokens */}
            <Controller
              name="max_tokens"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  fullWidth
                  size="small"
                  type="number"
                  label="Max Tokens"
                  inputProps={{ min: 1, max: 32000 }}
                  error={!!errors.max_tokens}
                  helperText={errors.max_tokens?.message}
                />
              )}
            />

            {/* Max history */}
            <Controller
              name="max_history"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  onChange={(e) => field.onChange(Number(e.target.value))}
                  fullWidth
                  size="small"
                  type="number"
                  label="Max History (messaggi)"
                  inputProps={{ min: 1, max: 50 }}
                  error={!!errors.max_history}
                  helperText={errors.max_history?.message}
                />
              )}
            />
          </div>

          {/* Flow selector — depends on the selected tenant */}
          <Controller
            name="flow_id"
            control={control}
            render={({ field }) => (
              <FlowSelect
                value={field.value ?? null}
                onChange={field.onChange}
                flows={flows}
                disabled={!selectedTenantId}
              />
            )}
          />

          <Box sx={{ display: "flex", gap: 1 }}>
            <Button
              variant="outlined"
              onClick={() => navigate("/chatbot/chatbots")}
              disabled={isSubmitting}
            >
              Annulla
            </Button>
            <Button variant="contained" type="submit" disabled={isSubmitting}>
              {isSubmitting ? <CircularProgress size={20} /> : "Crea chatbot"}
            </Button>
          </Box>
        </form>
      </Box>
    </div>
  );
}
